import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import './About.css';

const About: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="about-hero-text"
          >
            <h1>{t('about.hero.title')}</h1>
            <p className="about-hero-subtitle">
              {t('about.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founding Philosophy Section */}
      <section className="founding-philosophy-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
          >
            <h2 className="section-title">{t('about.foundingPhilosophy.title')}</h2>
            <p className="section-subtitle">{t('about.foundingPhilosophy.subtitle')}</p>
            <p className="section-intro">{t('about.foundingPhilosophy.intro')}</p>
            <p className="section-description">{t('about.foundingPhilosophy.description1')}</p>
            <p className="section-description">{t('about.foundingPhilosophy.description2')}</p>
            <p className="principle-text">{t('about.foundingPhilosophy.principle')}</p>
            <p className="section-description">{t('about.foundingPhilosophy.description3')}</p>
            <blockquote className="philosophy-quote">
              {t('about.foundingPhilosophy.quote')}
            </blockquote>
            <p className="section-conclusion">{t('about.foundingPhilosophy.conclusion')}</p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
            <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
            >
            <h2 className="section-title">{t('about.mission.title')}</h2>
            <p className="section-subtitle">{t('about.mission.subtitle')}</p>
            <p className="section-intro">{t('about.mission.intro')}</p>
            <ul className="responsibilities-list">
              <li>{t('about.mission.responsibilities.collective')}</li>
              <li>{t('about.mission.responsibilities.aesthetics')}</li>
              <li>{t('about.mission.responsibilities.vision')}</li>
            </ul>
            <p className="section-description">{t('about.mission.description')}</p>
            <ul className="criteria-list">
              <li>{t('about.mission.criteria.behavior')}</li>
              <li>{t('about.mission.criteria.cultural')}</li>
              <li>{t('about.mission.criteria.balance')}</li>
              <li>{t('about.mission.criteria.quality')}</li>
            </ul>
            <p className="section-conclusion">{t('about.mission.conclusion')}</p>
            </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="vision-section">
        <div className="container">
            <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            className="section-content"
            >
            <h2 className="section-title">{t('about.vision.title')}</h2>
            <p className="section-subtitle">{t('about.vision.subtitle')}</p>
            <p className="section-intro">{t('about.vision.intro')}</p>
            <p className="section-description">{t('about.vision.goal')}</p>
            <h3 className="targets-title">{t('about.vision.targets.title')}</h3>
            <ul className="targets-list">
              <li>{t('about.vision.targets.countries')}</li>
              <li>{t('about.vision.targets.projects')}</li>
              <li>{t('about.vision.targets.continents')}</li>
              <li>{t('about.vision.targets.reference')}</li>
              <li>{t('about.vision.targets.school')}</li>
            </ul>
            </motion.div>
        </div>
      </section>

      {/* Global Structure Section */}
      <section className="global-structure-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
          >
            <h2 className="section-title">{t('about.globalStructure.title')}</h2>
            <p className="section-subtitle">{t('about.globalStructure.subtitle')}</p>
            <p className="section-intro">{t('about.globalStructure.intro')}</p>
            <p className="section-description">{t('about.globalStructure.description')}</p>
            <div className="features-grid">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="feature-card"
              >
                <h4>{t('about.globalStructure.features.planning.title')}</h4>
                <p>{t('about.globalStructure.features.planning.description')}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="feature-card"
              >
                <h4>{t('about.globalStructure.features.execution.title')}</h4>
                <p>{t('about.globalStructure.features.execution.description')}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="feature-card"
              >
                <h4>{t('about.globalStructure.features.adaptation.title')}</h4>
                <p>{t('about.globalStructure.features.adaptation.description')}</p>
          </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="feature-card"
              >
                <h4>{t('about.globalStructure.features.assurance.title')}</h4>
                <p>{t('about.globalStructure.features.assurance.description')}</p>
              </motion.div>
          </div>
          </motion.div>
        </div>
      </section>

      {/* Core Expertise Section */}
      <section className="expertise-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
          >
            <h2 className="section-title">{t('about.expertise.title')}</h2>
            <p className="section-intro">{t('about.expertise.intro')}</p>
            <h3 className="subsection-title">{t('about.expertise.turnkey.title')}</h3>
            <ul className="expertise-list">
              {(t('about.expertise.turnkey.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <h3 className="subsection-title">{t('about.expertise.scope.title')}</h3>
            <ul className="expertise-list">
              {(t('about.expertise.scope.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="certifications-section">
        <div className="container">
              <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
              >
            <h2 className="section-title">{t('about.certifications.title')}</h2>
            <p className="section-intro">{t('about.certifications.intro')}</p>
            <h3 className="subsection-title">{t('about.certifications.international.title')}</h3>
            <ul className="certifications-list">
              {(t('about.certifications.international.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="section-note">{t('about.certifications.note')}</p>
              </motion.div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="differentiators-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
          >
            <h2 className="section-title">{t('about.differentiators.title')}</h2>
            <p className="section-intro">{t('about.differentiators.intro')}</p>
            <div className="differentiators-grid">
              <div className="differentiator-box">
                <h4 className="differentiator-title negative">{t('about.differentiators.not.title')}</h4>
                <ul className="differentiator-list">
                  {(t('about.differentiators.not.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="differentiator-box">
                <h4 className="differentiator-title positive">{t('about.differentiators.does.title')}</h4>
                <ul className="differentiator-list">
                  {(t('about.differentiators.does.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Client Relationships Section */}
      <section className="client-relationships-section">
        <div className="container">
            <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
            >
            <h2 className="section-title">{t('about.clientRelationships.title')}</h2>
            <p className="section-intro">{t('about.clientRelationships.intro')}</p>
            <ul className="relationships-list">
              {(t('about.clientRelationships.features', { returnObjects: true }) as string[]).map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
            </motion.div>
        </div>
      </section>

      {/* Why Nish Global Section */}
      <section className="why-nish-section">
        <div className="container">
            <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
            >
            <h2 className="section-title">{t('about.whyNishGlobal.title')}</h2>
            <p className="section-intro">{t('about.whyNishGlobal.intro')}</p>
            <ul className="why-list">
              {(t('about.whyNishGlobal.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="section-conclusion">{t('about.whyNishGlobal.conclusion')}</p>
            </motion.div>
        </div>
      </section>

      {/* Collaboration Section */}
      <section className="collaboration-section">
        <div className="container">
            <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="section-content"
            >
            <h2 className="section-title">{t('about.collaboration.title')}</h2>
            <p className="section-description">{t('about.collaboration.description')}</p>
            <p className="section-conclusion">{t('about.collaboration.conclusion')}</p>
            </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="cta-content"
          >
            <h2>{t('about.cta.title')}</h2>
            <p>
              {t('about.cta.description')}
            </p>
            <motion.a
              href="https://wa.me/905408072727?text=Merhaba,%20proje%20hakkında%20bilgi%20almak%20istiyorum."
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cta-button"
            >
              {t('common.contact')}
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
